import { SalaryEntryDto, CreateSalaryEntryPremiumFundRetentionDto, SalarySummaryDto, SalaryPayOrderDto, CreateSalaryEntryPremiumFundIssuanceDto, CreateSalaryPercentageSupervisorDto, SalaryCommissionRowDto } from '@sr/dto';
import { salaryApi } from 'entities/salary/salary.api';

export type SalaryEntryQuery = {
  year: number;
  month: number;
  userId: number;
};

export type PeriodQuery = {
  year: number,
  month: number,
  managerId?: number,
};

export type SalarySummaryQuery = Partial<SalaryEntryQuery>;

const injectedApi = salaryApi.injectEndpoints({
  endpoints: (builder) => ({
    getSalaryEntries: builder.query<SalaryEntryDto[], SalaryEntryQuery>({
      query: ({ year, month, userId }: SalaryEntryQuery) => ({
        url: `entries/${year}/${month}/${userId}`
      })
    }),
    getSalaryCommission: builder.query<SalaryCommissionRowDto[], SalaryEntryQuery>({
      query: ({ year, month, userId }: SalaryEntryQuery) => ({
        url: `commission/${year}/${month}/${userId}`
      }),
      providesTags: (result, error, query) => (result ? [{ type: 'salary-entries', id: `${query.year}-${query.month}-${query.userId}` }] : ['salary-entries'])
    }),
    getSummary: builder.query<SalarySummaryDto[], SalarySummaryQuery>({
      query: ({ year, month }: SalarySummaryQuery) => ({
        url: `summary/${year}/${month}`
      })
    }),
    getPremiumFund: builder.query({
      query: ({ year, month, managerId }: PeriodQuery) => ({
        url: `premium-fund/${year}/${month}/${managerId}`
      }),
      providesTags: (result, error, { year, month }) => 
        result ? [{ type: 'premiumFund', id: `${year}-${month}` }] : ['premiumFund']
    }),
    createPremiumFundRetention: builder.mutation<SalaryEntryDto, CreateSalaryEntryPremiumFundRetentionDto>({
      query: (dto) => ({
        url: 'premium-fund/retention',
        body: dto,
        method: 'POST'
      }),
      invalidatesTags: (result, error, dto) => [{ type: 'premiumFund', id: `${dto.year}-${dto.month}` }, 'salary-entries']
    }),
    createPremiumFundIssuance: builder.mutation<SalaryEntryDto, CreateSalaryEntryPremiumFundIssuanceDto>({
      query: (dto) => ({
        url: 'premium-fund/issuance',
        body: dto,
        method: 'POST'
      }),
      invalidatesTags: (result, error, dto) => [{ type: 'premiumFund', id: `${dto.year}-${dto.month}` }, 'salary-entries']
    }),
    getPayOrderSalary: builder.query<SalaryPayOrderDto, SalaryEntryQuery>({
      query: ({ year, month, userId }: SalaryEntryQuery) => ({
        url: `entries-payorder/${year}/${month}/${userId}`
      })
    }),
    createPayOrderWages: builder.mutation<void, number>({
      query: (salaryEntryId) => ({
        url: `pay-order-wages/${salaryEntryId}`,
        method: 'POST'
      }),
      invalidatesTags: ['periods', 'salary-entries', 'premiumFund', 'percentageSupervisor']
    }),
    deletePayOrderWages: builder.mutation<void, number>({
      query: (salaryEntryId) => ({
        url: `pay-order-wages/${salaryEntryId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['periods', 'salary-entries', 'premiumFund', 'percentageSupervisor']
    }),
    deleteSalaryEntry: builder.mutation<void, SalaryEntryQuery & { entryId: number }>({
      invalidatesTags: ['periods', 'salary-entries', 'premiumFund'],
      query: ({ entryId }) => ({
        url: `entries/${entryId}`,
        method: 'DELETE'
      }),
      async onQueryStarted({ year, month, userId, entryId }, { dispatch, queryFulfilled, getCacheEntry, getState }) {
        const patchResult = dispatch(
          injectedApi.util.updateQueryData('getSalaryEntries', { year, month, userId }, (draft) => {
            const index = draft.findIndex((entry) => entry.id === entryId);
            if (index !== -1) {
              draft.splice(index, 1);
            }
          })
        );
        try {
          await queryFulfilled;
        } catch (e) {
          patchResult.undo();
          console.error('RTK Query onQueryStarted error', e);
        }
      }
    }),
    getPercentageSupervisor: builder.query({
      query: ({ year, month }: PeriodQuery) => ({
        url: `percentage-supervisor/${year}/${month}`
      }),
      providesTags: (result, error, { year, month }) => 
        result ? [{ type: 'percentageSupervisor', id: `${year}-${month}` }] : ['percentageSupervisor']
    }),
    createPercentageSupervisor: builder.mutation<SalaryEntryDto, CreateSalaryPercentageSupervisorDto>({
      query: (dto) => ({
        url: 'percentage-supervisor/create',
        body: dto,
        method: 'POST'
      }),
      invalidatesTags: (result, error, dto) => [{ type: 'percentageSupervisor', id: `${dto.year}-${dto.month}` }, 'salary-entries']
    }),
  })
});

export const salaryEntriesApi = injectedApi.enhanceEndpoints({
  addTagTypes: ['summary', 'periods'],
  endpoints: {
    getSalaryEntries: {
      providesTags: (result, error, query) => (result ? [{ type: 'salary-entries', id: `${query.year}-${query.month}-${query.userId}` }] : ['salary-entries'])
    },
    getSummary: {
      providesTags: (result, error, query) => [{ type: 'summary', id: `${query.year}-${query.month}-${query.userId}` }, 'salary-entries']
    },
    getPayOrderSalary: {
      providesTags: (result, error, query) => [{ type: 'periods', id: `${query.year}-${query.month}-${query.userId}` }, { type: 'salary-entries', id: `${query.year}-${query.month}-${query.userId}` }]
    }
  }
});
